/**
 * Every credential decision in the console, in one module.
 *
 * Today (`AUTH_MODE = 'password'`) the browser holds the shared POC admin password in
 * sessionStorage and sends it as `x-admin-password` on every `/admin/*` call, exactly as
 * before — decision-api compares it timing-safely and answers 503 when it is unset.
 *
 * Next (`AUTH_MODE = 'cookie'`) the BFF owns the credential: the browser holds NO password
 * at all, sends `credentials: 'same-origin'` so the session cookie rides along, and adds an
 * `X-CSRF-Token` header read from the non-HttpOnly companion cookie. The BFF attaches
 * `x-admin-password` to the decision API server-side, where it belongs.
 *
 * The cutover is this one constant plus the BFF's base path — no component, and no call
 * site in `api.ts`, has to change. That is the whole point of this file.
 *
 * NOTE: values are never logged, never put in a URL, and never leave this origin.
 */

import { ADMIN_PASSWORD_HEADER } from '@kkp-fraud/shared';

export type AuthMode = 'password' | 'cookie';

/**
 * `VITE_AUTH_MODE=cookie` flips the console onto the BFF. Anything else — including unset —
 * keeps today's behaviour, so an existing deployment cannot change auth by accident.
 */
export const AUTH_MODE: AuthMode = import.meta.env?.VITE_AUTH_MODE === 'cookie' ? 'cookie' : 'password';

/** sessionStorage, not localStorage: closing the tab signs you out. Password mode only. */
const PASSWORD_KEY = 'kkp.rule-admin.password';

/** The CSRF cookie the BFF sets alongside the HttpOnly session cookie. Cookie mode only. */
const CSRF_COOKIE = 'kkp_csrf';
const CSRF_HEADER = 'X-CSRF-Token';

export function getAdminPassword(): string | null {
  if (AUTH_MODE !== 'password') return null;
  try {
    return window.sessionStorage.getItem(PASSWORD_KEY);
  } catch {
    return null; // private window, blocked site data — the login screen just reappears
  }
}

export function setAdminPassword(password: string): void {
  if (AUTH_MODE !== 'password') return;
  try {
    window.sessionStorage.setItem(PASSWORD_KEY, password);
  } catch {
    /* ignore — the session still works, it just will not survive a reload */
  }
}

export function clearAdminPassword(): void {
  try {
    window.sessionStorage.removeItem(PASSWORD_KEY);
  } catch {
    /* ignore */
  }
}

/** Reads the CSRF token the BFF wrote as a readable cookie. Cookie mode only. */
export function getCsrfToken(): string | null {
  if (AUTH_MODE !== 'cookie') return null;
  try {
    const hit = document.cookie
      .split(';')
      .map((c) => c.trim())
      .find((c) => c.startsWith(`${CSRF_COOKIE}=`));
    return hit ? decodeURIComponent(hit.slice(CSRF_COOKIE.length + 1)) : null;
  } catch {
    return null;
  }
}

/**
 * Whether the app should render the control panel rather than the login screen, WITHOUT
 * asking the server.
 *
 * In cookie mode this is deliberately optimistic: the browser cannot see the HttpOnly
 * session cookie, so the only honest answer is "try a call and see". A 401 from any call
 * lands on `onUnauthorised` and drops the user back to the login screen, which is the same
 * path a wrong password already takes today.
 */
export function isAuthenticated(): boolean {
  return AUTH_MODE === 'cookie' ? true : getAdminPassword() !== null;
}

/** True when this mode has a client-side credential to forget on sign-out. */
export function holdsClientCredential(): boolean {
  return AUTH_MODE === 'password';
}

/**
 * The auth half of every `fetch` init — the ONLY place that decides what a request carries.
 * `api.ts` merges this into each call; nothing else builds auth headers.
 */
export function authRequestInit(): { headers: Record<string, string>; credentials?: RequestCredentials } {
  if (AUTH_MODE === 'cookie') {
    const csrf = getCsrfToken();
    return {
      // The session cookie rides along; the password never exists in this browser.
      credentials: 'same-origin',
      headers: csrf === null ? {} : { [CSRF_HEADER]: csrf },
    };
  }

  const password = getAdminPassword();
  return { headers: password === null ? {} : { [ADMIN_PASSWORD_HEADER]: password } };
}

/** Called after a 401 and on explicit sign-out. Cookie mode has nothing local to drop. */
export function forgetCredential(): void {
  if (AUTH_MODE === 'password') clearAdminPassword();
}
